/* eslint-disable @nx/enforce-module-boundaries */
import { PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { planActions } from '../plan';
import { COOKIE_NAME } from './types';
import { serviceabilityActions } from '../serviceability';
import { appActions } from '../app';
import { emptyOrRedacted, isDefined, isDefinedString, validateUrlParam } from '../utils';
import { getCookie } from '@buy-viasat/utils';
import { Country } from '@buy-viasat/types/build/bv';
import { addressActions, AddressQueryParams } from '../address';
import { USE_NEW_API } from '../../../../packages/client/src/shared/utils/dataFetchingHooks/constants';

const parseQueryParams = (url: string) => {
  const searchParams = new URLSearchParams(url);

  return Array.from(searchParams).reduce(
    (acc: { [key: string]: string }, [currKey, currValue]: [string, string]) => ({
      ...acc,
      [currKey.toLowerCase()]: currValue || '',
    }),
    {},
  );
};

const URL_PARAMS = {
  DID: { maxLength: 12, action: appActions.setDealerIdParam },
  PAID: { maxLength: 40, action: appActions.setPartnerIdParam },
  affiliateID: { maxLength: 25, action: appActions.setAffiliateIdParam },
  sessionID: { maxLength: 40, action: appActions.setSessionIdParam },
  CID: { maxLength: 25, action: appActions.setCampaignIdParam },
};

export const parseQueryString = (
  /* Examines URL for predefined address input and other defined params. 
  If minimum input criteria for scrubbing address is present (ie. addressLine1, postalCode, & countryCode),
  then the address is scrubbed to check for address validity & user is routed to Plans if the address is valid.
  All other cases (ie. no predefined address, invalid address, incomplete address, ect.) set serviceability loader to false & do nothing. */
  dispatch: Dispatch<PayloadAction<string | boolean | AddressQueryParams | undefined>>,
  queryString: string,
  isServiceAddressSet: boolean,
  country?: Country,
  scrubAddress?: (input: { address: Record<string, string | string[]> }) => Promise<void>,
): void => {
  const parsedParams = parseQueryParams(queryString);
  const {
    plnid: PLNID,
    affiliateid: affiliateID,
    sessionid: sessionID,
    cid: CID,
    offerid: offerId,
    region,
    municipality,
    ...restParams
  } = parsedParams;
  let {
    did: DID,
    paid: PAID,
    addressline1: addressLine1,
    addressline2: addressLine2,
    postalcode: postalCode,
    countrycode: countryCode,
    hidecta: hideCta,
  } = restParams;

  // Get the country code from state if countryCode isn't passed in
  if (!isDefinedString(countryCode)) countryCode = country as string;
  if (hideCta !== 'true') hideCta = '';

  if (!(DID || PAID)) {
    DID = getCookie(COOKIE_NAME.dealerId) as string;
    PAID = getCookie(COOKIE_NAME.partnerId) as string;
  }

  if (PLNID) {
    dispatch(planActions.setPreassignedPlanId(PLNID));
  }

  const paramValues = { DID, PAID, affiliateID, sessionID, CID };
  Object.entries(URL_PARAMS).forEach(([key, config]) => {
    const value = paramValues[key as keyof typeof paramValues];
    if (validateUrlParam(value, config.maxLength)) {
      dispatch(config.action(value));
    }
  });

  if (offerId) {
    dispatch(appActions.setOfferIdParam(offerId));
  }

  let isCompleteAddress = [addressLine1, postalCode, countryCode].every(isDefinedString);

  if (!isCompleteAddress) {
    addressLine1 = getCookie(COOKIE_NAME.addressLine1) as string;
    addressLine2 = !isDefined(addressLine2) ? (getCookie(COOKIE_NAME.addressLine2) as string) : addressLine2;
    postalCode = !isDefined(postalCode) ? (getCookie(COOKIE_NAME.postalCode) as string) : postalCode;
    isCompleteAddress = [addressLine1, postalCode, countryCode].every(isDefined);
    dispatch(serviceabilityActions.setIsServiceabilityLoading(false));
  }

  dispatch(
    addressActions.setAddressQueryParams({
      addressLine1: emptyOrRedacted(addressLine1),
      addressLine2: emptyOrRedacted(addressLine2),
      municipality,
      region,
      postalCode: emptyOrRedacted(postalCode),
      countryCode,
    }),
  );

  dispatch(appActions.setToggleMfeCTA(!!hideCta));
  dispatch(appActions.logURLParams());

  if (!isCompleteAddress) {
    return;
  }

  if (isServiceAddressSet) {
    return;
  }

  const serviceAddress = {
    addressLines: [addressLine1, addressLine2],
    municipality,
    region,
    postalCode,
    countryCode,
  };

  dispatch(serviceabilityActions.setServiceAddress(serviceAddress));

  if (USE_NEW_API && scrubAddress) {
    const addressLines = [addressLine1, addressLine2].filter(isDefined);
    scrubAddress({ address: { ...serviceAddress, addressLines } });
  } else {
    dispatch(serviceabilityActions.scrubServiceAddress());
  }

  dispatch(
    serviceabilityActions.updateScrubLocationAddressLine2(addressLine1 + addressLine2 ? ` ${addressLine2}` : ''),
  );
  dispatch(serviceabilityActions.isServiceAddressSet(true));
};
